import { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Lock, Save, Eye, EyeOff, CheckCircle, Contrast } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/authService';
import { useAccessibility } from '../contexts/AccessibilityContext';

type Tab = 'personal' | 'password' | 'accesibilidad';

export default function ProfilePage() {
  const { user, profile, setProfile } = useAuthStore();
  const { highContrast, toggleHighContrast } = useAccessibility();
  const [tab, setTab] = useState<Tab>('personal');

  // Personal data form
  const [form, setForm] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    phone: profile?.phone || '',
    municipio: profile?.municipio || '',
    birthday: profile?.birthday ? profile.birthday.split('T')[0] : '',
  });
  const [saving, setSaving] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [savingPw, setSavingPw] = useState(false);

  async function handleSavePersonal(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await authService.updateProfile({
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim() || undefined,
        phone: form.phone.trim() || undefined,
        municipio: form.municipio.trim() || undefined,
        birthday: form.birthday || undefined,
      });
      setProfile({ ...profile!, ...form });
      toast.success('Perfil actualizado');
    } catch {
      toast.error('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (pwForm.newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setSavingPw(true);
    try {
      await authService.changePassword({
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
      toast.success('Contraseña cambiada correctamente');
    } catch {
      toast.error('Contraseña actual incorrecta');
    } finally {
      setSavingPw(false);
    }
  }

  const displayName = profile?.first_name
    ? `${profile.first_name} ${profile.last_name || ''}`.trim()
    : user?.username || '';

  const initials = displayName
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>

          {/* Header / Avatar */}
          <div className="flex items-center gap-5 mb-8">
            <div className="w-16 h-16 rounded-full bg-neureka-yellow/20 border-2 border-neureka-yellow/40 flex items-center justify-center text-2xl font-bold text-neureka-yellow select-none">
              {initials || <User className="w-7 h-7" />}
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold">{displayName || 'Mi perfil'}</h1>
              <p className="text-white/50 text-sm">{user?.email} · <span className="capitalize">{user?.rol}</span></p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-white/5 rounded-xl p-1">
            {([
              { id: 'personal', label: 'Datos personales', icon: User },
              { id: 'password', label: 'Contraseña', icon: Lock },
              { id: 'accesibilidad', label: 'Accesibilidad', icon: Contrast },
            ] as const).map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  tab === id
                    ? 'bg-neureka-yellow text-neureka-dark'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* TAB: Datos personales */}
          {tab === 'personal' && (
            <motion.form
              key="personal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleSavePersonal}
              className="glass-container space-y-5"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/60 mb-1.5">Nombre</label>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="Tu nombre"
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-neureka-yellow"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-1.5">Apellidos</label>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="Tus apellidos"
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-neureka-yellow"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-1.5">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white/40 cursor-not-allowed"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/60 mb-1.5">Teléfono</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="600 000 000"
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-neureka-yellow"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-1.5">Municipio</label>
                  <input
                    type="text"
                    value={form.municipio}
                    onChange={e => setForm(f => ({ ...f, municipio: e.target.value }))}
                    placeholder="Tu ciudad"
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-neureka-yellow"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-1.5">Fecha de nacimiento</label>
                <input
                  type="date"
                  value={form.birthday}
                  onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-neureka-yellow [color-scheme:dark]"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 bg-neureka-yellow text-neureka-dark font-bold rounded-xl hover:bg-yellow-400 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {saving ? (
                  <span className="animate-pulse">Guardando...</span>
                ) : (
                  <><Save className="w-4 h-4" /> Guardar cambios</>
                )}
              </button>
            </motion.form>
          )}

          {/* TAB: Contraseña */}
          {tab === 'password' && (
            <motion.form
              key="password"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleChangePassword}
              className="glass-container space-y-5"
            >
              {[
                { key: 'currentPassword' as const, label: 'Contraseña actual', show: showPw.current, toggle: () => setShowPw(s => ({ ...s, current: !s.current })) },
                { key: 'newPassword' as const, label: 'Nueva contraseña', show: showPw.new, toggle: () => setShowPw(s => ({ ...s, new: !s.new })) },
                { key: 'confirm' as const, label: 'Confirmar nueva contraseña', show: showPw.confirm, toggle: () => setShowPw(s => ({ ...s, confirm: !s.confirm })) },
              ].map(({ key, label, show, toggle }) => (
                <div key={key}>
                  <label className="block text-sm text-white/60 mb-1.5">{label}</label>
                  <div className="relative">
                    <input
                      type={show ? 'text' : 'password'}
                      value={pwForm[key]}
                      onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                      required
                      className="w-full px-3 py-2.5 pr-10 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-neureka-yellow"
                    />
                    <button
                      type="button"
                      onClick={toggle}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                    >
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}

              {/* Indicador de coincidencia */}
              {pwForm.confirm && (
                <div className={`flex items-center gap-2 text-sm ${pwForm.newPassword === pwForm.confirm ? 'text-green-400' : 'text-red-400'}`}>
                  <CheckCircle className="w-4 h-4" />
                  {pwForm.newPassword === pwForm.confirm ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}
                </div>
              )}

              <button
                type="submit"
                disabled={savingPw}
                className="w-full py-3 bg-neureka-yellow text-neureka-dark font-bold rounded-xl hover:bg-yellow-400 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {savingPw ? (
                  <span className="animate-pulse">Cambiando...</span>
                ) : (
                  <><Lock className="w-4 h-4" /> Cambiar contraseña</>
                )}
              </button>
            </motion.form>
          )}

          {/* TAB: Accesibilidad */}
          {tab === 'accesibilidad' && (
            <motion.div
              key="accesibilidad"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass-container space-y-6"
            >
              <div>
                <h2 className="text-lg font-semibold mb-1">Opciones de accesibilidad visual</h2>
                <p className="text-white/60 text-sm">
                  Ajustes para mejorar la visibilidad de la plataforma.
                </p>
              </div>

              {/* Toggle alto contraste */}
              <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-white/10 bg-white/5">
                <div className="flex-1">
                  <p className="font-medium">Modo alto contraste</p>
                  <p className="text-sm text-white/50 mt-0.5">
                    Aumenta el contraste de colores y elimina gradientes para facilitar la lectura.
                  </p>
                </div>
                <button
                  onClick={() => {
                    toggleHighContrast();
                    toast.success(highContrast ? 'Modo normal activado' : 'Alto contraste activado');
                  }}
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 transition-colors duration-200 focus:outline-none ${
                    highContrast
                      ? 'bg-neureka-yellow border-neureka-yellow'
                      : 'bg-white/20 border-white/30'
                  }`}
                  role="switch"
                  aria-checked={highContrast}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 rounded-full shadow-lg transition-transform duration-200 ${
                      highContrast
                        ? 'translate-x-5 bg-black'
                        : 'translate-x-0 bg-white'
                    }`}
                  />
                </button>
              </div>

              <p className="text-xs text-white/40">
                Esta preferencia se guarda automáticamente en tu dispositivo.
              </p>
            </motion.div>
          )}

        </motion.div>
      </div>
    </div>
  );
}
